/**
 * Phase 2B.3.1 — "Share your portal" card.
 *
 * Surfaced on the admin home so new agents instantly know they have a public
 * URL — and have a one-click way to copy it for SMS/email/social signatures.
 *
 * Behaviour:
 *   • If on theporchlight.io → show theporchlight.io/<slug>/
 *   • Otherwise (preview pod, custom domain like sellwitherika.com) → show
 *     the current origin so the URL actually works for the agent's clients.
 *   • Hides itself if the agent has no slug yet (legacy account, e.g. Erika).
 */
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useContent } from "../lib/content";
import { Copy, Check, Link2, Sparkles, X } from "lucide-react";

const FIRST_PORTAL_SEEN_KEY = "porchlight_first_portal_celebrated";
const FRESH_AGENT_WINDOW_MS = 60 * 60 * 1000; // 1 hour

export default function SharePortalCard() {
  const { user } = useAuth();
  const { content } = useContent();
  const [copied, setCopied] = useState(false);

  const portalUrl = useMemo(() => {
    if (!user?.agent_slug) return null;
    const host = (typeof window !== "undefined" && window.location.hostname) || "";
    // On the production marketing host, we know theporchlight.io routes by slug.
    // Anywhere else (preview, sellwitherika.com, custom domains), use current origin.
    if (host === "theporchlight.io" || host === "www.theporchlight.io") {
      return `https://theporchlight.io/${user.agent_slug}/`;
    }
    if (typeof window !== "undefined") {
      return `${window.location.origin}/${user.agent_slug}/`;
    }
    return `https://theporchlight.io/${user.agent_slug}/`;
  }, [user?.agent_slug]);

  if (!portalUrl) return null;
  // Sellers and impersonated views never see this.
  if (user?.role !== "admin" || user?.impersonated_by) return null;

  const onCopy = async () => {
    let ok = false;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(portalUrl);
        ok = true;
      }
    } catch { /* clipboard permission blocked, fall through */ }
    if (!ok) {
      // Fallback for headless / http / restricted contexts: spawn a hidden
      // textarea, select, execCommand('copy'). Old but ubiquitous.
      try {
        const ta = document.createElement("textarea");
        ta.value = portalUrl;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        ok = true;
      } catch { /* ignore */ }
    }
    // Always confirm visually so users know the action registered, even if
    // the underlying copy failed (they can right-click → copy from the URL pill).
    setCopied(true);
    setTimeout(() => setCopied(false), 2200);
    return ok;
  };

  const firstName = content?.branding?.agent_first_name || "your";
  const display = portalUrl.replace(/^https?:\/\//, "").replace(/\/$/, "");

  return (
    <>
      <FirstPortalCelebration user={user} firstName={firstName} />
      <div data-testid="share-portal-section"
           style={{ marginTop: 20 }}>
      <div data-testid="share-portal-card" className="card"
           style={{
             padding: "20px 24px",
             display: "flex",
             gap: 18,
             alignItems: "flex-start",
             flexWrap: "wrap",
             background: "linear-gradient(180deg, #FFFCF6 0%, #F9F2E3 100%)",
             borderColor: "#E8DEC4",
           }}>
        <span style={{
          width: 40, height: 40, borderRadius: 12,
          background: "var(--secondary)", color: "#fff",
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0,
        }}>
          <Link2 size={18} />
        </span>
        <div style={{ flex: 1, minWidth: 220 }}>
          <div className="overline" style={{ marginBottom: 2 }}>
            Share {firstName === "your" ? "your" : `${firstName}'s`} portal
          </div>
          <p style={{ margin: 0, fontSize: 14, color: "var(--text-muted)", lineHeight: 1.5 }}>
            Drop this link in your email signature, text it to a new client, or share it on social.
            They'll see your branding before they sign in.
            {" "}
            <Link to="/admin/content"
                  data-testid="share-portal-edit-branding-link"
                  style={{ color: "var(--primary)", fontWeight: 500, textDecoration: "underline", textDecorationStyle: "dotted", textUnderlineOffset: 3 }}>
              Edit your branding →
            </Link>
          </p>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10, flexWrap: "wrap" }}>
            <code data-testid="share-portal-url"
                  style={{
                    padding: "6px 10px",
                    borderRadius: 8,
                    background: "rgba(255,255,255,0.7)",
                    border: "1px solid #E8DEC4",
                    fontSize: 13,
                    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                    color: "var(--text)",
                    letterSpacing: "0.01em",
                    wordBreak: "break-all",
                  }}>
              {display}
            </code>
            <button
              data-testid="share-portal-copy"
              onClick={onCopy}
              className="btn-secondary"
              style={{
                padding: "6px 14px",
                fontSize: 13,
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                transition: "background-color 160ms ease",
              }}
            >
              {copied ? (<><Check size={14} /> Copied</>) : (<><Copy size={14} /> Copy link</>)}
            </button>
          </div>
        </div>
      </div>

      </div>
    </>
  );
}

/**
 * Phase 2B.3.2 — first-portal celebration ribbon.
 *
 * Rendered above the share card the FIRST time a freshly-signed-up agent
 * lands on /admin within their first hour. Sets a sessionStorage flag so
 * it doesn't repeat on subsequent navigations within the session.
 */
function FirstPortalCelebration({ user, firstName }) {
  // Lazy initializer runs ONCE per mount and survives strict-mode re-mounts
  // because we don't write the sessionStorage flag here — only on dismiss.
  const [show, setShow] = useState(() => {
    if (!user?.created_at) return false;
    if (user.role !== "admin" || user.impersonated_by) return false;
    const created = new Date(user.created_at).getTime();
    if (Number.isNaN(created)) return false;
    if (Date.now() - created > FRESH_AGENT_WINDOW_MS) return false;
    try {
      const sessionKey = `${FIRST_PORTAL_SEEN_KEY}_${user.id}`;
      if (window.sessionStorage.getItem(sessionKey) === "1") return false;
    } catch { /* ignore */ }
    return true;
  });

  if (!show) return null;

  const dismiss = () => {
    try {
      const sessionKey = `${FIRST_PORTAL_SEEN_KEY}_${user.id}`;
      window.sessionStorage.setItem(sessionKey, "1");
    } catch { /* ignore */ }
    setShow(false);
  };
  const display = firstName === "your" ? "your" : firstName + "'s";

  return (
    <div data-testid="first-portal-celebration"
         className="first-portal-celebration"
         role="status"
         aria-live="polite"
         style={{
           marginTop: 20,
           padding: "16px 20px",
           borderRadius: 14,
           background: "linear-gradient(110deg, #2B4C3F 0%, #3E6A57 50%, #6B5B95 100%)",
           color: "#FFFCF6",
           display: "flex",
           alignItems: "center",
           gap: 14,
           boxShadow: "0 12px 32px -16px rgba(43,76,63,0.5)",
           overflow: "hidden",
           position: "relative",
         }}>
      <span aria-hidden="true" style={{
        width: 36, height: 36, borderRadius: 12,
        background: "rgba(255,255,255,0.15)",
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0,
      }}>
        <Sparkles size={18} className="celebration-sparkle" />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="font-serif"
             style={{ fontSize: 18, fontWeight: 500, letterSpacing: "-0.005em", marginBottom: 2 }}>
          You just made {display} portal.
        </div>
        <div style={{ fontSize: 13, color: "rgba(255,252,246,0.78)", lineHeight: 1.45 }}>
          Share the link below to bring in your first sellers — or take a look around first.
        </div>
      </div>
      <button
        data-testid="first-portal-dismiss"
        onClick={dismiss}
        aria-label="Dismiss"
        style={{
          background: "transparent",
          border: "none",
          color: "rgba(255,252,246,0.7)",
          cursor: "pointer",
          padding: 6,
          borderRadius: 8,
          display: "inline-flex",
          transition: "background-color 160ms ease, color 160ms ease",
        }}
        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.12)"; e.currentTarget.style.color = "#fff"; }}
        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; e.currentTarget.style.color = "rgba(255,252,246,0.7)"; }}
      >
        <X size={16} />
      </button>
    </div>
  );
}

